import API from '../api/api'
import cloneDeep from 'lodash/cloneDeep';
import { FIELD_ID_SEPARATOR, resourceIdOf } from '../utils/translatableResourceFields';

/**
 * One slice per section key. Every action carries the section it belongs to.
 */

const LOAD_RESET_QUERY = 'TRANSLATABLE_RESOURCES_LOAD_RESET_QUERY'
const LOAD_PENDING = 'TRANSLATABLE_RESOURCES_LOAD_PENDING'
const LOAD_RESPONSE = 'TRANSLATABLE_RESOURCES_LOAD_RESPONSE'

const SAVE_PENDING = 'TRANSLATABLE_RESOURCES_SAVE_PENDING'
const SAVE_RESPONSE = 'TRANSLATABLE_RESOURCES_SAVE_RESPONSE'
const SAVE_ABORTED = 'TRANSLATABLE_RESOURCES_SAVE_ABORTED'

const RESET_ERRORS = 'TRANSLATABLE_RESOURCES_RESET_ERRORS'

const initialSectionState = {
  load_pending: false,
  save_pending: false,
  resources: [],
  // per type, never per section: a multi-type section loads sequentially into this
  // slice, so whichever type landed last would decide a single cursor
  cursors: {},
  // a page was given up on, so what is loaded is not the whole section
  incomplete: false,
  error: false,
  saveErrors: [],
}

const initialState = {}

const getProgress = (translations) => {
  if(!translations || translations.length === 0) {
    return 0;
  }
  const translated = translations.filter(x => x.target !== null && x.target !== '').length;
  return (translated / translations.length) * 100;
}

// REDUCER
export default (state = initialState, action) => {
  if(!action.section) {
    return state;
  }

  const section = state[action.section] ? state[action.section] : initialSectionState;
  const update = (changes) => ({ ...state, [action.section]: { ...section, ...changes } });

  switch(action.type) {

    case LOAD_RESET_QUERY: {
      return update({ resources: [], cursors: {}, incomplete: false, saveErrors: [] })
    }
    case LOAD_PENDING: {
      return update({ load_pending: true })
    }
    case LOAD_RESPONSE: {
      if(action.response.success !== true) {
        // a later page failing is not worth losing the pages that did arrive over. shopify
        // rejects its own cursor on some types, and level 1 would close the open section
        if(action.continuation === true) {
          return update({
            load_pending: false,
            cursors: { ...section.cursors, [action.resourceType]: null },
            incomplete: true,
          })
        }

        return update({
          load_pending: false,
          // this type has no usable cursor, but leave the other types' alone
          cursors: { ...section.cursors, [action.resourceType]: null },
          error: {
            message: action.response,
            action: action.action,
            level: 1,
          },
        })
      }

      const data = action.response.data;
      const resources = cloneDeep(section.resources);

      // GID => {resource, translations}, translations keyed by field key. an empty page is legal
      Object.keys(data.translatable_resources || {}).forEach(gid => {
        if(resources.find(x => x.shopify_id === gid)) {
          return;
        }
        const entry = data.translatable_resources[gid];
        resources.push({
          shopify_id: gid,
          display_name: entry.resource.display_name,
          progress: entry.resource.progress,
          updated_at: entry.resource.updated_at,
          translations: Object.keys(entry.translations || {}).map(key => ({
            ...entry.translations[key]
          })),
        })
      });

      return update({
        load_pending: false,
        resources: resources,
        // cursor is false when the list is exhausted
        cursors: { ...section.cursors, [action.resourceType]: data.cursor ? data.cursor : null },
      })
    }

    case SAVE_PENDING: {
      const resources = cloneDeep(section.resources);
      resources.forEach(item => {
        item.translations.forEach(translation => {
          translation.error = false;
        })
      })
      return update({ resources: resources, save_pending: true, saveErrors: [] })
    }
    case SAVE_ABORTED: {
      return update({ save_pending: false, error: false })
    }

    case SAVE_RESPONSE: {
      // The response echoes what was written for the language the save was issued for. The
      // abort races a response already on the wire, so it can still land after the merchant
      // selected another language - applying it then is what puts one language's text under another.
      if(action.stale === true) {
        return update({ save_pending: false })
      }

      if(!action.response || !action.response.data) {
        return update({
          save_pending: false,
          error: {
            message: action.response,
            action: action.action,
            level: 1,
          },
        })
      }

      const resources = cloneDeep(section.resources);
      // ItemsList's error snackbar groups by item and jumps by field id, so one entry per section
      // with the flat field id as object_key - that is what the deep link and the restore lookup
      // both resolve against
      const errorsByObject = {};
      const results = action.response.data.translatable_resources || {};

      Object.keys(results).forEach(gid => {
        const item = resources.find(x => x.shopify_id === gid);
        if(!item) {
          return;
        }

        Object.keys(results[gid].translations || {}).forEach(key => {
          const result = results[gid].translations[key];
          const translation = item.translations.find(x => x.key === key);
          if(!translation) {
            return;
          }

          if(result.success === true) {
            translation.target = result.action === 'add' ? result.target : null;
            translation.error = false;
          } else {
            // keep the submitted value visible and offer the previous one for restore
            translation.error = true;
            const submitted = action.resources[gid] &&
              action.resources[gid].translations.find(x => x.key === key);
            translation.restore = translation.target;
            translation.target = submitted ? submitted.target : translation.target;
            if(!errorsByObject[action.section]) {
              errorsByObject[action.section] = { object: action.section, errors: [] };
            }
            errorsByObject[action.section].errors.push({
              object_key: `${resourceIdOf(gid)}${FIELD_ID_SEPARATOR}${key}`,
              // the backend tags shopify's validation errors; fall back to the raw message so the
              // snackbar still says something rather than nothing
              validation_errors: result.validation_errors && Object.keys(result.validation_errors).length > 0
                ? result.validation_errors
                : { error_520_unknown: result.message || '' },
            });
          }
        });

        item.progress = getProgress(item.translations);
      });

      return update({ resources: resources, save_pending: false, saveErrors: Object.values(errorsByObject) })
    }

    case RESET_ERRORS: {
      return update({ error: false, saveErrors: [] })
    }
    default:
      return state
  }
}

export const getSectionStateFrom = (slices, sectionKey) =>
  slices && slices[sectionKey] ? slices[sectionKey] : initialSectionState;

export const getSectionState = (state, sectionKey) =>
  getSectionStateFrom(state.translatableResources, sectionKey);

/**
 * The next page to ask for, and which type to ask it of. Null when every type of the
 * section is exhausted, which is also the state before anything has loaded
 */
export const getSectionNextPage = (sectionState, section) => {
  const resourceType = section.resourceTypes.find(type => sectionState.cursors[type]);
  return resourceType
    ? { resourceType: resourceType, cursor: sectionState.cursors[resourceType] }
    : null;
}

// every type has answered and none has a cursor left, so a progress recompute may be trusted.
// a type that has not reported yet and a chain that gave up early both have to fail this
export const isSectionLoadedInFull = (sectionState, section) =>
  sectionState.incomplete !== true
  && section.resourceTypes.every(type => type in sectionState.cursors && !sectionState.cursors[type]);


// ACTIONS CREATORS
export const resetErrors = (sectionKey) => {
  return dispatch => {
    dispatch({
      type: RESET_ERRORS,
      section: sectionKey,
    });
  }
}

export const loadTranslatableResources = (sectionKey, resourceType, languageId, options = {}, resetQuery = false, callback) => {
  return dispatch => {
    if(resetQuery === true) {
      dispatch({
        type: LOAD_RESET_QUERY,
        section: sectionKey,
      })
    }

    dispatch({
      type: LOAD_PENDING,
      section: sectionKey,
    })

    API.translatableResourcesByType(
      resourceType,
      languageId,
      options,
      response => {
        // A dropped load is not dispatched at all: the switch that dropped it has already
        // started the load for the language now on screen, and that one owns the pending flag.
        // The callback still fires - the section loader chains on it.
        if(response && response.aborted === true) {
          if(callback) {
            callback(response);
          }
          return;
        }

        dispatch({
          type: LOAD_RESPONSE,
          section: sectionKey,
          resourceType: resourceType,
          response: response,
          continuation: options.cursor ? true : false,
          action: () => loadTranslatableResources(sectionKey, resourceType, languageId, options, resetQuery, callback),
        });
        if(callback) {
          callback(response);
        }
      }
    )
  }
}

/**
 * Loads every resource type of a section into its slice. Sequential, so the pending
 * flag never reads as settled while a type is still in flight - the reducer appends
 * and dedupes by GID, so the types accumulate
 *
 * options.loadAllPages follows the cursor until each type is exhausted. Opt-in, because a
 * caller offering a load more button wants only the first page - without one, page two is
 * otherwise unreachable
 */
export const loadTranslatableResourceSection = (section, languageId, options = {}, resetQuery = false, callback) => {
  const { loadAllPages, ...requestOptions } = options;

  return dispatch => {
    // a backend repeating a cursor would otherwise spin here, since the reducer dedupes
    // by GID and the page count would never grow
    const seen = new Set();

    const loadType = (index, reset, cursor) => {
      if(index >= section.resourceTypes.length) {
        if(callback) {
          callback();
        }
        return;
      }

      dispatch(loadTranslatableResources(
        section.key,
        section.resourceTypes[index],
        languageId,
        cursor ? { ...requestOptions, cursor: cursor } : requestOptions,
        reset,
        response => {
          // The types after this one would otherwise still load the language just left, into the
          // slice the new language's load has meanwhile reset.
          if(response && response.aborted === true) {
            if(callback) {
              callback(response);
            }
            return;
          }

          const next = response && response.data ? response.data.cursor : null;

          if(loadAllPages === true && next && !seen.has(next)) {
            seen.add(next);
            loadType(index, false, next);
            return;
          }

          loadType(index + 1, false);
        }
      ));
    }
    loadType(0, resetQuery);
  }
}

export const saveTranslatableResources = (sectionKey, languageId, resources, callback) => {
  return (dispatch, getState) => {
    dispatch({
      type: SAVE_PENDING,
      section: sectionKey,
    });

    API.translatableResourcesSave(
      languageId,
      resources,
      response => {
        if(response && response.aborted === true) {
          dispatch({
            type: SAVE_ABORTED,
            section: sectionKey,
          })
          return;
        }

        dispatch({
          type: SAVE_RESPONSE,
          section: sectionKey,
          response: response,
          resources: resources,
          stale: getState().shop.shop.edit_language !== languageId,
          action: () => saveTranslatableResources(sectionKey, languageId, resources, callback),
        });
        if(callback) {
          callback(response);
        }
      }
    )
  }
}
